import React, { useState } from 'react';
import { authService } from '../services/authService';

function ChangePassword({ onClose, onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!currentPassword) {
      setError('Current password is required');
      return;
    }

    if (!newPassword) {
      setError('New password is required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      setLoading(true);
      const response = await authService.changePassword({
        currentPassword,
        newPassword
      });
      
      // Check if the response indicates success
      if (response && response.statusCode === 200) {
        onSuccess?.();
        onClose();
      } else {
        // Handle unexpected response format or server-side errors
        const errorMessage = response?.error || response?.message || 'Unexpected response from server. Please try again.';
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Password change error:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to change password. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.error || error.response.data?.message || `Server error: ${error.response.status}`;
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '#666' };
    
    let score = 0;
    if (password.length >= 8) score = 1;
    if (password.length >= 12) score = 2;
    if (password.length >= 16) score = 3;

    if (score === 1) return { strength: score, label: 'Good', color: '#ffc107' };
    if (score === 2) return { strength: score, label: 'Better', color: '#2ed573' };
    if (score === 3) return { strength: score, label: 'Strong', color: '#2ed573' };
    return { strength: 0, label: 'Too Short', color: '#ff4757' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }}>
      <div className="card" style={{ 
        width: '450px', 
        margin: 0,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h3 style={{ 
            color: '#ff6b35', 
            margin: '0',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            🔒 Change Password
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#b0b0b0',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ff6b35'}
            onMouseLeave={(e) => e.target.style.color = '#b0b0b0'}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <div style={{ 
              color: '#ff4757', 
              backgroundColor: 'rgba(255, 71, 87, 0.1)',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid rgba(255, 71, 87, 0.3)',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <div className="form-group">
            <label>Current Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
                required
                disabled={loading}
                style={{ paddingRight: '45px' }}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#b0b0b0',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                {showPasswords.current ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                required
                disabled={loading}
                style={{ paddingRight: '45px' }}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#b0b0b0',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                {showPasswords.new ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            
            {newPassword && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px'
                }}>
                  <span style={{ fontSize: '12px', color: '#b0b0b0' }}>
                    Password Strength
                  </span>
                  <span style={{ 
                    fontSize: '12px', 
                    color: passwordStrength.color,
                    fontWeight: '600'
                  }}>
                    {passwordStrength.label}
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(passwordStrength.strength / 3) * 100}%`,
                    height: '100%',
                    backgroundColor: passwordStrength.color,
                    transition: 'all 0.3s ease',
                    borderRadius: '2px'
                  }}></div>
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
                disabled={loading}
                style={{ 
                  paddingRight: '45px',
                  borderColor: confirmPassword && newPassword !== confirmPassword ? '#ff4757' : undefined
                }}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#b0b0b0',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                {showPasswords.confirm ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <small style={{ color: '#ff4757', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Passwords do not match
              </small>
            )}
          </div>

          <div style={{ 
            background: 'rgba(52, 152, 219, 0.1)',
            border: '1px solid rgba(52, 152, 219, 0.2)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '24px'
          }}>
            <div style={{ color: '#3498db', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Password Requirements:
            </div>
            <ul style={{ 
              color: '#b0b0b0', 
              fontSize: '12px', 
              margin: '0', 
              paddingLeft: '16px',
              lineHeight: '1.5'
            }}>
              <li>At least 8 characters long</li>
              <li>Different from current password</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              type="submit" 
              className="btn" 
              style={{ flex: 1 }}
              disabled={loading || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              className="btn" 
              style={{ 
                flex: 1, 
                background: 'linear-gradient(135deg, #666, #555)',
                boxShadow: '0 2px 8px rgba(102, 102, 102, 0.3)'
              }}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChangePassword;